import {
  initializeApp,
  FirebaseApp,
  FirebaseOptions
} from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  Auth
} from 'firebase/auth';
import {
  getFirestore,
  initializeFirestore,
  connectFirestoreEmulator,
  Firestore
} from 'firebase/firestore';
import {
  getStorage,
  connectStorageEmulator,
  FirebaseStorage
} from 'firebase/storage';
import {
  getFunctions,
  connectFunctionsEmulator,
  Functions
} from 'firebase/functions';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getAnalytics, Analytics } from 'firebase/analytics';

const getEnv = (key: string): string | boolean | undefined => {
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      const val = import.meta.env[key];
      return typeof val === 'string' ? val.trim() : (val as boolean | undefined);
    }
  } catch {
    // Fallback for environments where import.meta is not available
  }
  return undefined;
};

const USE_EMULATORS = getEnv('VITE_USE_EMULATORS') === 'true';

declare global {
  interface Window {
    FIREBASE_APPCHECK_DEBUG_TOKEN?: string;
  }
}

if (getEnv('DEV') && typeof window !== 'undefined') {
  window.FIREBASE_APPCHECK_DEBUG_TOKEN = '550e8400-e29b-41d4-a716-446655440000';
}

let app: FirebaseApp | null = null;
let authInstance: Auth | null = null;
let dbInstance: Firestore | null = null;
let storageInstance: FirebaseStorage | null = null;
let functionsInstance: Functions | null = null;
let analyticsInstance: Analytics | null = null;

const initializeFirebase = (): FirebaseApp | null => {
  if (app) return app;

  try {
    const firebaseConfig: FirebaseOptions = {
      apiKey: (getEnv('VITE_FIREBASE_API_KEY') as string) || (USE_EMULATORS ? 'fake-api-key' : undefined),
      authDomain: (getEnv('VITE_FIREBASE_AUTH_DOMAIN') as string) || (USE_EMULATORS ? 'localhost' : undefined),
      projectId: (getEnv('VITE_FIREBASE_PROJECT_ID') as string) || (USE_EMULATORS ? 'fastrack-driving-school-lms' : undefined),
      storageBucket: (getEnv('VITE_FIREBASE_STORAGE_BUCKET') as string) || (USE_EMULATORS ? 'fastrack-driving-school-lms.appspot.com' : undefined),
      messagingSenderId: (getEnv('VITE_FIREBASE_MESSAGING_SENDER_ID') as string) || (USE_EMULATORS ? '123456789' : undefined),
      appId: (getEnv('VITE_FIREBASE_APP_ID') as string) || (USE_EMULATORS ? '1:123456789:web:abcdef123456' : undefined)
    };

    const measurementId = getEnv('VITE_FIREBASE_MEASUREMENT_ID') as string | undefined;
    if (measurementId) {
      firebaseConfig.measurementId = measurementId;
    }

    // Force Emulator Project ID in Development to ensure Emulator compatibility
    // ONLY if VITE_USE_EMULATORS is set to 'true'
    if (getEnv('DEV') && USE_EMULATORS) {
      firebaseConfig.projectId = 'fastrack-driving-school-lms';
    }

    if (Object.values(firebaseConfig).some(v => !v)) {
      if (getEnv('MODE') !== 'test') {
        console.error('Firebase configuration incomplete');
      }
      return null;
    }

    app = initializeApp(firebaseConfig);

    // Initialize services
    authInstance = getAuth(app);

    // Enable long polling only for emulators to fix connection issues
    if (getEnv('DEV') && USE_EMULATORS) {
      dbInstance = initializeFirestore(app, { experimentalForceLongPolling: true });
    } else {
      dbInstance = getFirestore(app);
    }

    functionsInstance = getFunctions(app);
    storageInstance = getStorage(app);

    // Skip analytics in development or emulator mode to avoid inflating production data
    const isProduction = getEnv('PROD') === true || getEnv('MODE') === 'production';
    if (measurementId && isProduction && !USE_EMULATORS) {
      try {
        analyticsInstance = getAnalytics(app);
      } catch (error) {
        console.warn('Failed to initialize Firebase Analytics:', error);
      }
    }

    if (getEnv('DEV')) {
      console.warn('FIREBASE PROJECT ID:', firebaseConfig.projectId);
      console.warn('USE_EMULATORS:', USE_EMULATORS);
      console.warn('DEV MODE:', getEnv('DEV'));
    }

    // Connect to emulators in development if toggle is enabled
    if (getEnv('DEV') && USE_EMULATORS) {
      console.warn('🔧 Connecting to Firebase Emulators (127.0.0.1)...');

      if (authInstance) {
        connectAuthEmulator(authInstance, 'http://127.0.0.1:9099');
        console.warn('✅ Auth Emulator Connected');
      }

      if (dbInstance) {
        connectFirestoreEmulator(dbInstance, '127.0.0.1', 8080);
        console.warn('✅ Firestore Emulator Connected');
      }

      if (functionsInstance) {
        connectFunctionsEmulator(functionsInstance, '127.0.0.1', 5001);
        console.warn('✅ Functions Emulator Connected');
      }

      if (storageInstance) {
        connectStorageEmulator(storageInstance, '127.0.0.1', 9199);
        console.warn('✅ Storage Emulator Connected');
      }
    }

  } catch (error) {
    console.error('Failed to initialize Firebase:', error);
  }
  return app;
};

const initializeAppCheckConfig = (): void => {
  const firebaseApp = initializeFirebase();
  if (firebaseApp) {
    // Skip App Check if using emulator project
    if (getEnv('VITE_USE_EMULATORS') === 'true') {
      console.warn('⚠️ Skipping App Check initialization for emulator project');
      return;
    }

    const siteKey = getEnv('VITE_FIREBASE_APP_CHECK_SITE_KEY');
    if (siteKey) {
      try {
        initializeAppCheck(firebaseApp, {
          provider: new ReCaptchaV3Provider(siteKey as string),
          isTokenAutoRefreshEnabled: true
        });

        if (getEnv('DEV')) {
          console.warn('✓ Firebase App Check initialized with ReCaptcha provider');
        }
      } catch (error) {
        console.error('Failed to initialize App Check:', error);
      }
    } else if (getEnv('DEV')) {
      console.warn('Firebase App Check site key not configured');
    }
  }
};

export const getAuthInstance = (): Auth => {
  initializeFirebase();
  if (!authInstance) throw new Error('Firebase Auth not initialized');
  return authInstance;
};

export const getDbInstance = (): Firestore => {
  initializeFirebase();
  if (!dbInstance) throw new Error('Firebase Firestore not initialized');
  return dbInstance;
};

export const getStorageInstance = (): FirebaseStorage => {
  initializeFirebase();
  if (!storageInstance) throw new Error('Firebase Storage not initialized');
  return storageInstance;
};

export const getFunctionsInstance = (): Functions => {
  initializeFirebase();
  if (!functionsInstance) throw new Error('Firebase Functions not initialized');
  return functionsInstance;
};

export const getAnalyticsInstance = (): Analytics | null => {
  const isProduction = getEnv('PROD') === true || getEnv('MODE') === 'production';
  if (!analyticsInstance && (getEnv('VITE_FIREBASE_MEASUREMENT_ID') as string) && isProduction && !USE_EMULATORS) {
    const firebaseApp = initializeFirebase();
    if (firebaseApp) {
      try {
        analyticsInstance = getAnalytics(firebaseApp);
      } catch (error) {
        console.warn('Failed to get Analytics instance:', error);
      }
    }
  }
  return analyticsInstance;
};

initializeFirebase();
initializeAppCheckConfig();

// Safe top-level initialization for backward compatibility
// These will throw if accessed before initialized, but we try-catch the initial call
// to avoid crashing the build process if env vars are missing during resolution.
let auth: Auth = null as any;
let db: Firestore = null as any;
let storage: FirebaseStorage = null as any;
let functions: Functions = null as any;
let analytics: Analytics | null = null;

try {
  auth = getAuthInstance();
  db = getDbInstance();
  storage = getStorageInstance();
  functions = getFunctionsInstance();
  analytics = getAnalyticsInstance();
} catch (e) {
  if (import.meta.env.PROD) {
    console.warn('Firebase legacy exports initialized with errors. This is expected if env vars are missing during build.');
  }
}

export { auth, db, storage, functions, analytics };

export const getApp = (): FirebaseApp | null => initializeFirebase();

export default {
  get auth() { return getAuthInstance(); },
  get db() { return getDbInstance(); },
  get storage() { return getStorageInstance(); },
  get functions() { return getFunctionsInstance(); },
  get analytics() { return getAnalyticsInstance(); }
};
